import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import { Input, PasswordInput } from "../components/ui/Input";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";
import { useFormState } from "../hooks/useFormState";
import {
  validatePassword,
  validatePasswordConfirmation,
} from "../utils/validationUtils";

const AccountSettings = () => {
  const { user, logout, updateUser } = useAuth();

  // Memoize initial form data to prevent unnecessary recalculations
  const initialProfileData = useMemo(() => {
    if (!user) return { firstName: "", lastName: "", email: "" };
    
    return {
      firstName: user.firstName || user.first_name || "",
      lastName: user.lastName || user.last_name || "",
      email: user.email || "",
    };
  }, [user?.firstName, user?.first_name, user?.lastName, user?.last_name, user?.email]);

  const {
    formData: profileData,
    handleChange: handleProfileChange,
    resetForm: resetProfileForm,
  } = useFormState(initialProfileData);

  const {
    formData: passwordData,
    handleChange: handlePasswordChange,
    resetForm: resetPasswordForm,
  } = useFormState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotifications: true,
    passwordResetEmails: true,
    pickReminderEmails: true,
    weeklySummaryEmails: true,
  });

  // Track if preferences have been fetched to prevent duplicate calls
  const [preferencesFetched, setPreferencesFetched] = useState(false);

  const { loading: profileLoading, withLoading: withProfileLoading } =
    useLoadingState();
  const { loading: passwordLoading, withLoading: withPasswordLoading } =
    useLoadingState();
  const { loading: notificationLoading, withLoading: withNotificationLoading } =
    useLoadingState();
  const [profileError, setProfileError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [notificationError, setNotificationError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [notificationSuccess, setNotificationSuccess] = useState("");

  // Memoize the fetch function to prevent recreation on every render
  const fetchNotificationPreferences = useCallback(async () => {
    // Prevent duplicate API calls
    if (preferencesFetched) return;
    
    try {
      const response = await apiService.get("/notifications/preferences");
      setNotificationPreferences(response.preferences);
      setPreferencesFetched(true);
    } catch (err) {
      // Silently handle error for notification preferences
    }
  }, [preferencesFetched]);

  // Initialize form with user data when user is loaded
  useEffect(() => {
    if (user && !preferencesFetched) {
      fetchNotificationPreferences();
    }
  }, [user, preferencesFetched, fetchNotificationPreferences]);

  // Reset form when user data changes
  useEffect(() => {
    if (user && initialProfileData.firstName && initialProfileData.lastName && initialProfileData.email) {
      resetProfileForm(initialProfileData);
    }
  }, [user, initialProfileData, resetProfileForm]);

  // Reset preferences fetched flag when user changes
  useEffect(() => {
    setPreferencesFetched(false);
  }, [user?.id]); // Only reset when user ID changes

  // Handle notification preference changes with useCallback to prevent unnecessary re-renders
  const handleNotificationChange = useCallback((key, value) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  // Update notification preferences
  const updateNotificationPreferences = useCallback(async () => {
    setNotificationError("");
    setNotificationSuccess("");

    try {
      await withNotificationLoading(async () => {
        return await apiService.put("/notifications/preferences", notificationPreferences);
      });

      setNotificationSuccess("Notification preferences updated successfully");
      
      // Reset the fetched flag to allow fresh data fetching if needed
      setPreferencesFetched(false);
    } catch (err) {
      apiService.handleError(err, setNotificationError, "Failed to update notification preferences");
    }
  }, [notificationPreferences, withNotificationLoading, setPreferencesFetched]);

  const updateProfile = useCallback(async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess("");

    try {
      const response = await withProfileLoading(async () => {
        return await apiService.patch("/users/profile", {
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          email: profileData.email,
        });
      });

      // Update the user context with new data
      const updatedUser = response;
      updateUser(updatedUser);

      setProfileSuccess("Profile updated successfully");
    } catch (err) {
      apiService.handleError(err, setProfileError, "Failed to update profile");
    }
  }, [profileData, withProfileLoading, updateUser]);

  const updatePassword = useCallback(async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    // Validate password confirmation
    const confirmationValidation = validatePasswordConfirmation(
      passwordData.newPassword,
      passwordData.confirmPassword,
    );
    if (!confirmationValidation.isValid) {
      setPasswordError(confirmationValidation.error);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(passwordData.newPassword);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.errors[0]);
      return;
    }

    try {
      await withPasswordLoading(async () => {
        return await apiService.patch("/users/password", {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        });
      });

      setPasswordSuccess("Password updated successfully");
      resetPasswordForm();
    } catch (err) {
      apiService.handleError(err, setPasswordError, "Failed to update password");
    }
  }, [passwordData, withPasswordLoading, resetPasswordForm]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  if (!user) {
    return <div>Loading...</div>;
  }

  // Don't render the form until we have the initial profile data
  if (!initialProfileData.firstName && !initialProfileData.lastName && !initialProfileData.email) {
    return <div>Loading user data...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-gray-600 mt-2">
          Manage your account information and preferences
        </p>
      </div>

      {/* Profile Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Profile Information
        </h2>
        {profileError && <Alert type="error" message={profileError} />}
        {profileSuccess && <Alert type="success" message={profileSuccess} />}
        
        <form onSubmit={updateProfile} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name"
              name="firstName"
              value={profileData.firstName}
              onChange={handleProfileChange}
              required
            />
            <Input
              label="Last Name"
              name="lastName"
              value={profileData.lastName}
              onChange={handleProfileChange}
              required
            />
          </div>
          <Input
            label="Email"
            name="email"
            type="email"
            value={profileData.email}
            onChange={handleProfileChange}
            required
          />
          <Button
            type="submit"
            loading={profileLoading}
            className="w-full md:w-auto"
          >
            Update Profile
          </Button>
        </form>
      </div>

      {/* Notification Preferences Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Notification Preferences
        </h2>
        
        {notificationError && <Alert type="error" message={notificationError} />}
        {notificationSuccess && <Alert type="success" message={notificationSuccess} />}
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Email Notifications</h3>
              <p className="text-sm text-gray-600">
                Receive email notifications for important updates
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={notificationPreferences.emailNotifications}
                onChange={(e) => handleNotificationChange("emailNotifications", e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Pick Reminder Emails</h3>
              <p className="text-sm text-gray-600">
                Daily reminders when you have games to pick
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={notificationPreferences.pickReminderEmails}
                onChange={(e) => handleNotificationChange("pickReminderEmails", e.target.checked)}
                disabled={!notificationPreferences.emailNotifications}
              />
              <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                !notificationPreferences.emailNotifications ? 'bg-gray-200 opacity-50' : 'bg-gray-200'
              }`}></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Weekly Summary Emails</h3>
              <p className="text-sm text-gray-600">
                Weekly performance summaries and league updates
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={notificationPreferences.weeklySummaryEmails}
                onChange={(e) => handleNotificationChange("weeklySummaryEmails", e.target.checked)}
                disabled={!notificationPreferences.emailNotifications}
              />
              <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                !notificationPreferences.emailNotifications ? 'bg-gray-200 opacity-50' : 'bg-gray-200'
              }`}></div>
            </label>
          </div>
        </div>

        <div className="mt-6">
          <Button
            onClick={updateNotificationPreferences}
            loading={notificationLoading}
            className="w-full md:w-auto"
          >
            Save Notification Preferences
          </Button>
        </div>
      </div>

      {/* Password Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Change Password
        </h2>
        {passwordError && <Alert type="error" message={passwordError} />}
        {passwordSuccess && <Alert type="success" message={passwordSuccess} />}
        
        <form onSubmit={updatePassword} className="space-y-4">
          <PasswordInput
            label="Current Password"
            name="currentPassword"
            value={passwordData.currentPassword}
            onChange={handlePasswordChange}
            required
          />
          <PasswordInput
            label="New Password"
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            required
          />
          <PasswordInput
            label="Confirm New Password"
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
            required
          />
          <Button
            type="submit"
            loading={passwordLoading}
            className="w-full md:w-auto"
          >
            Update Password
          </Button>
        </form>
      </div>

      {/* Logout Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Account Actions
        </h2>
        <Button
          onClick={handleLogout}
          variant="danger"
          className="w-full md:w-auto"
        >
          Logout
        </Button>
      </div>
    </div>
  );
};

export default AccountSettings;
